import { createVNode, render } from 'vue';
import BalloonTips from './index.vue';

/**
 * Toast 配置选项
 */
interface ToastOptions {
  message?: string;
  type?: 'success' | 'error' | 'warning' | 'info';
  duration?: number;
  className?: string;
}

/**
 * Toast 实例
 */
interface ToastInstance {
  show: () => void;
  hide: () => void;
  destroy: () => void;
}

/**
 * 创建 Toast 实例
 * @param options 配置选项
 * @returns Toast 实例
 */
function createToast(options: ToastOptions): ToastInstance {
  const container = document.createElement('div');
  document.body.appendChild(container);

  const vnode = createVNode(BalloonTips, {
    ...options,
    onDestroy: () => {
      render(null, container);
      document.body.removeChild(container);
    }
  });

  render(vnode, container);

  const instance = vnode.component?.exposed as ToastInstance;

  return {
    show: () => instance?.show(),
    hide: () => instance?.hide(),
    destroy: () => {
      instance?.hide();
      setTimeout(() => {
        render(null, container);
        if (document.body.contains(container)) {
          document.body.removeChild(container);
        }
      }, 300);
    }
  };
}

/**
 * Toast 主函数
 * @param message 消息内容或配置对象
 * @param options 配置选项（当第一个参数是字符串时使用）
 * @returns Toast 实例
 */
function Toast(message: string | ToastOptions, options?: ToastOptions): ToastInstance {
  let config: ToastOptions;
  
  if (typeof message === 'string') {
    config = {
      message,
      ...options
    };
  } else {
    config = message;
  }

  const instance = createToast(config);
  instance.show();
  return instance;
}

/**
 * 成功提示
 * @param message 消息内容
 * @param options 其他选项
 * @returns Toast 实例
 */
Toast.success = (message: string, options?: Omit<ToastOptions, 'message' | 'type'>): ToastInstance => {
  return Toast({
    message,
    type: 'success',
    ...options
  });
};

/**
 * 错误提示
 * @param message 消息内容
 * @param options 其他选项
 * @returns Toast 实例
 */
Toast.error = (message: string, options?: Omit<ToastOptions, 'message' | 'type'>): ToastInstance => {
  return Toast({
    message,
    type: 'error',
    ...options
  });
};

/**
 * 警告提示
 * @param message 消息内容
 * @param options 其他选项
 * @returns Toast 实例
 */
Toast.warning = (message: string, options?: Omit<ToastOptions, 'message' | 'type'>): ToastInstance => {
  return Toast({
    message,
    type: 'warning',
    ...options
  });
};

/**
 * 信息提示
 * @param message 消息内容
 * @param options 其他选项
 * @returns Toast 实例
 */
Toast.info = (message: string, options?: Omit<ToastOptions, 'message' | 'type'>): ToastInstance => {
  return Toast({
    message,
    type: 'info',
    ...options
  });
};

/**
 * 清除所有 Toast
 */
Toast.clear = (): void => {
  const containers = document.querySelectorAll('[data-toast]');
  containers.forEach(container => {
    if (container.parentNode) {
      container.parentNode.removeChild(container);
    }
  });
};

export default Toast;
export type { ToastOptions, ToastInstance }; 